import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";

export default function AdminAssignments() {
  const [briefs, setBriefs] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);

  const load = () => {
    Promise.all([
      api.get("/briefs"),
      api.get("/designers"),
      api.get("/orders"),
      api.get("/vendors"),
    ]).then(([b, d, o, v]) => {
      setBriefs(b.data); setDesigners(d.data); setOrders(o.data); setVendors(v.data);
    });
  };
  useEffect(load, []);

  const assignDesigner = async (briefId, designerId) => {
    if (!designerId) return;
    try {
      await api.post(`/briefs/${briefId}/assign-designer`, { designer_id: designerId });
      toast.success("Designer assigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const assignVendor = async (orderId, vendorId) => {
    if (!vendorId) return;
    try {
      await api.post(`/orders/${orderId}/assign-vendor`, { vendor_id: vendorId });
      toast.success("Vendor assigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Vendor users (role=vendor) + legacy vendor records combined
  const [vendorUsers, setVendorUsers] = useState([]);
  useEffect(() => {
    // Not exposed via a listing endpoint; derive from designers-style pattern via /vendors combined
    api.get("/designers").catch(()=>{});
  }, []);

  const needDesigner = briefs.filter(b => b.design_source === "human" && !b.assigned_designer_id);
  const needVendor = orders.filter(o => !o.vendor_id);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Admin · Assignments</div>
        <h1 className="text-5xl font-black tracking-tighter mb-10">Route work.</h1>

        {/* Assign designers */}
        <section className="mb-12">
          <div className="pf-label mb-2">Queue · Designer assignment</div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Briefs awaiting designer</h2>
          <p className="text-[var(--pf-text-2)] mb-6 max-w-2xl">Client requested a human designer. Pick one from your roster to route the brief.</p>
          {needDesigner.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-12 text-center text-[var(--pf-text-2)]" data-testid="no-designer-assignments">
              🎉 All caught up. No briefs waiting for a designer.
            </div>
          ) : (
            <div className="border-2 border-[var(--pf-brand)]">
              {needDesigner.map(b => (
                <div key={b.id} className="grid grid-cols-12 gap-4 items-center p-5 border-b last:border-0 border-[var(--pf-border)] bg-[var(--pf-brand)]/5" data-testid={`assign-brief-${b.id}`}>
                  <div className="col-span-5">
                    <div className="font-mono text-xs text-[var(--pf-text-2)]">#{b.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold mt-1">{b.title}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">{b.product_type.replace('_',' ')} · {b.industry} · {b.quantity} pcs</div>
                  </div>
                  <div className="col-span-4 text-xs text-[var(--pf-text-2)]">{b.description}</div>
                  <div className="col-span-3">
                    <select onChange={(e)=>assignDesigner(b.id, e.target.value)} defaultValue="" data-testid={`assign-d-${b.id}`}
                      className="border-2 border-[var(--pf-text)] px-3 py-2 text-xs uppercase tracking-widest font-bold bg-white w-full">
                      <option value="">Pick a designer…</option>
                      {designers.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name}{d.city ? ` · ${d.city}` : ""}{d.hourly_rate ? ` · ₹${d.hourly_rate}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Assign vendors */}
        <section className="mb-12">
          <div className="pf-label mb-2">Queue · Vendor assignment</div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Orders awaiting print vendor</h2>
          <p className="text-[var(--pf-text-2)] mb-6 max-w-2xl">Pick the best-match vendor by capability & location. They'll see the job in their dashboard.</p>
          {needVendor.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-12 text-center text-[var(--pf-text-2)]" data-testid="no-vendor-assignments">
              🎉 All orders routed.
            </div>
          ) : (
            <div className="border-2 border-[var(--pf-brand)]">
              {needVendor.map(o => (
                <div key={o.id} className="grid grid-cols-12 gap-4 items-center p-5 border-b last:border-0 border-[var(--pf-border)] bg-[var(--pf-brand)]/5" data-testid={`assign-order-${o.id}`}>
                  <div className="col-span-4">
                    <div className="font-mono text-xs text-[var(--pf-text-2)]">#{o.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold mt-1 capitalize">{o.product_type.replace('_',' ')} × {o.quantity}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">₹{o.total_amount?.toLocaleString()}</div>
                  </div>
                  <div className="col-span-4 text-xs text-[var(--pf-text-2)]">{o.delivery_address}</div>
                  <div className="col-span-4">
                    <select onChange={(e)=>assignVendor(o.id, e.target.value)} defaultValue="" data-testid={`assign-v-${o.id}`}
                      className="border-2 border-[var(--pf-text)] px-3 py-2 text-xs uppercase tracking-widest font-bold bg-white w-full">
                      <option value="">Pick a vendor…</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} · {v.city} {v.capacity ? `· ${v.capacity}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/orders" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors">
            <div className="pf-label">Go to</div><div className="text-xl font-black mt-2">Orders pipeline →</div>
          </Link>
          <Link to="/admin/vendors" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors">
            <div className="pf-label">Go to</div><div className="text-xl font-black mt-2">Vendor network →</div>
          </Link>
          <Link to="/admin/whatsapp" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors">
            <div className="pf-label">Go to</div><div className="text-xl font-black mt-2">WhatsApp bot →</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
